import { useCallback, useState } from "react";

type AssessmentStepMeta = {
  key: string;
  lastStep?: boolean;
  requiresCompletion?: boolean;
};

export function useAssessmentStepFlow(
  currentStep: AssessmentStepMeta,
  isLastStep: boolean,
) {
  const [stepReadyByKey, setStepReadyByKey] = useState<Record<string, boolean>>(
    {},
  );

  const canFinish = Boolean(currentStep.lastStep) || isLastStep;
  const isCurrentStepReady = currentStep.requiresCompletion
    ? Boolean(stepReadyByKey[currentStep.key])
    : true;

  const handleStepReadyChange = useCallback(
    (isReady: boolean) => {
      setStepReadyByKey((previous) => {
        if (previous[currentStep.key] === isReady) {
          return previous;
        }
        return {
          ...previous,
          [currentStep.key]: isReady,
        };
      });
    },
    [currentStep.key],
  );

  return {
    canFinish,
    isCurrentStepReady,
    handleStepReadyChange,
  };
}

export function resolveResumeStepIndex(
  steps: AssessmentStepMeta[],
  completedStepKeys: string[],
): number {
  const completed = new Set(
    completedStepKeys
      .map((stepKey) => stepKey.trim())
      .filter((stepKey) => stepKey.length > 0),
  );

  const firstIncompleteIndex = steps.findIndex(
    (step) => !completed.has(step.key.trim()),
  );

  if (firstIncompleteIndex >= 0) {
    return firstIncompleteIndex;
  }

  return Math.max(steps.length - 1, 0);
}

export function resolveFetchableStep<
  T extends AssessmentStepMeta & {
    requestStep?: string;
    shouldFetch?: boolean;
  },
>(steps: T[], currentStep: T): T {
  const stepByKey = new Map(steps.map((step) => [step.key, step]));

  let candidate: T | undefined = currentStep;
  const visited = new Set<string>();

  while (candidate) {
    if (candidate.shouldFetch !== false) {
      return candidate;
    }

    const requestStep = candidate.requestStep?.trim();
    if (!requestStep || visited.has(requestStep)) {
      break;
    }
    visited.add(requestStep);
    candidate = stepByKey.get(requestStep);
  }

  return currentStep;
}
